import { message } from 'antd'
import { useCallback, useRef, useState } from 'react'
import { translateImageGenerationPrompt } from '../../../../api'
import { useCanvasGenerationI18n } from '../useCanvasGenerationI18n'
import {
  preparePromptTextForTranslation,
  promptAppearsMostlyEnglish,
  reassemblePromptAfterTranslation,
} from '../imagePromptTranslation'

interface Props {
  workspaceId: string
  prompt: string
  onPromptChange: (next: string) => void
  visible: boolean
  disabled?: boolean
}

export default function ImagePromptTranslateTools({
  workspaceId,
  prompt,
  onPromptChange,
  visible,
  disabled = false,
}: Props) {
  const { t } = useCanvasGenerationI18n()
  const [translating, setTranslating] = useState(false)
  const [originalPrompt, setOriginalPrompt] = useState<string | null>(null)
  const inFlightRef = useRef(false)

  const handleTranslate = useCallback(async () => {
    const trimmed = (prompt || '').trim()
    if (!trimmed || inFlightRef.current) return

    if (promptAppearsMostlyEnglish(trimmed)) {
      message.info(t('generation.promptAlreadyEnglish'))
      return
    }

    const { textForTranslation, mentionPlaceholders } = preparePromptTextForTranslation(trimmed)
    if (!textForTranslation.trim()) return

    inFlightRef.current = true
    setTranslating(true)
    try {
      const { data: result } = await translateImageGenerationPrompt(workspaceId, {
        text: textForTranslation,
        source_language: 'auto',
        target_language: 'en',
      })
      if (result.already_english) {
        message.info(t('generation.promptAlreadyEnglish'))
        return
      }
      setOriginalPrompt(trimmed)
      const next = reassemblePromptAfterTranslation(result.translated_text, mentionPlaceholders)
      onPromptChange(next)
    } catch (err: unknown) {
      const apiErr = err as { message?: string }
      message.error(apiErr?.message || t('generation.promptTranslateFailed'))
    } finally {
      inFlightRef.current = false
      setTranslating(false)
    }
  }, [onPromptChange, prompt, t, workspaceId])

  const handleRestore = useCallback(() => {
    if (!originalPrompt) return
    onPromptChange(originalPrompt)
    setOriginalPrompt(null)
  }, [onPromptChange, originalPrompt])

  if (!visible) return null

  return (
    <div className="pcv-gen-prompt-tools nodrag" data-testid="image-prompt-translate-tools">
      <button
        type="button"
        className="pcv-gen-tool-btn"
        data-testid="image-prompt-translate-btn"
        disabled={disabled || translating || !prompt.trim()}
        onClick={() => void handleTranslate()}
        title={t('generation.promptTranslateTooltip')}
      >
        {translating ? t('generation.promptTranslating') : t('generation.promptTranslate')}
      </button>
      {originalPrompt ? (
        <button
          type="button"
          className="pcv-gen-tool-btn"
          data-testid="image-prompt-restore-btn"
          disabled={disabled || translating}
          onClick={handleRestore}
        >
          {t('generation.promptRestoreOriginal')}
        </button>
      ) : null}
    </div>
  )
}
